import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ResponseDeliverableSet } from '@/schema'
import { SortOrder } from '@/types/common'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import { DeliverableSetFilters, IInviteUserDeliverableSet } from './types'

// Deliverable sets store state and actions
interface DeliverableSetListStore {
  // State
  searchText: string
  filters: DeliverableSetFilters
  page: number
  rowsPerPage: number
  sort: {
    column: keyof ResponseDeliverableSet
    direction: SortOrder
  }
  editingDeliverableSet: ResponseDeliverableSet | null
  dialogOpen: boolean
  deletingDeliverableSet: ResponseDeliverableSet | null
  deleteDialogOpen: boolean
  contextCompanyId: string | null
  contextProjectId: string | null
  showSendApprovalDialog: boolean
  requestApprovalDeliverableSet: ResponseDeliverableSet | null
  showCancelApprovalDialog: boolean
  cancelApprovalDeliverableSet: ResponseDeliverableSet | null
  showDelegateDialog: boolean
  delegateDeliverableSet: ResponseDeliverableSet | null

  // Invite user dialog
  showInviteUserDialog: boolean
  inviteUserSearchText: string | null
  inviteUsers: IInviteUserDeliverableSet[]

  // Actions
  setSearchText: (searchText: string) => void
  setFilter: (payload: Partial<DeliverableSetFilters>) => void
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  setSort: (sort: {
    column: keyof ResponseDeliverableSet | null
    direction: SortOrder
  }) => void
  setEditingDeliverableSet: (item: ResponseDeliverableSet | null) => void
  clearEditingDeliverableSet: () => void
  setDialogOpen: (open: boolean, companyId?: string, projectId?: string) => void
  setDeletingDeliverableSet: (item: ResponseDeliverableSet | null) => void
  clearDeletingDeliverableSet: () => void
  setDeleteDialogOpen: (open: boolean) => void
  setSendToReviewDialogOpen: (open: boolean) => void
  setSendToReviewDeliverableSet: (item: ResponseDeliverableSet | null) => void
  clearSendToReviewDeliverableSet: () => void
  setCancelApprovalDialogOpen: (open: boolean) => void
  setCancelApprovalDeliverableSet: (item: ResponseDeliverableSet | null) => void
  clearCancelApprovalDeliverableSet: () => void
  setDelegateDialogOpen: (open: boolean) => void
  setDelegateDeliverableSet: (item: ResponseDeliverableSet | null) => void
  clearDelegateDeliverableSet: () => void
  resetFilters: () => void

  // Invite user dialog
  setInviteUserDialogOpen: (open: boolean) => void
  setInviteUserSearchText: (searchText: string) => void
  setInviteUsers: (users: IInviteUserDeliverableSet[]) => void
  updateInviteUserRole: (payload: {
    id?: string
    uuid?: string
    roleId: string
    roleName: string
    index: number
  }) => void

  reset: () => void
}

// Initial state
const initialState = {
  searchText: '',
  filters: {
    searchName: '',
    companyName: null,
    companyId: null,
    projectName: null,
    projectId: null,
    status: null,
  },
  page: 0,
  rowsPerPage: TABLE_PAGE_SIZE,
  sort: {
    column: 'created_at' as keyof ResponseDeliverableSet,
    direction: 'desc' as SortOrder,
  },
  editingDeliverableSet: null as ResponseDeliverableSet | null,
  dialogOpen: false,
  deletingDeliverableSet: null as ResponseDeliverableSet | null,
  deleteDialogOpen: false,
  contextCompanyId: null as string | null,
  contextProjectId: null as string | null,
  showSendApprovalDialog: false,
  requestApprovalDeliverableSet: null as ResponseDeliverableSet | null,
  showCancelApprovalDialog: false,
  cancelApprovalDeliverableSet: null as ResponseDeliverableSet | null,
  showDelegateDialog: false,
  delegateDeliverableSet: null as ResponseDeliverableSet | null,

  showInviteUserDialog: false,
  inviteUserSearchText: null,
  inviteUsers: [],
}

/**
 * Deliverable Sets Store
 *
 * Manages filter, search, pagination, and sorting state for deliverable sets.
 *
 * Features:
 * - Filter management (Company, Project, ReportingEvent)
 * - Search by name
 * - Pagination state
 * - Sorting state
 * - Reset functionality
 * - Redux DevTools integration
 *
 * @example
 * ```tsx
 * // In a component
 * const { filters, setFilter, setPage } = useDeliverableSetsStore()
 *
 * // Set a filter
 * setFilter('company', 'Zenith Dynamics')
 *
 * // Set search
 * setFilter('searchName', 'Deliverable Set 1')
 *
 * // Change page
 * setPage(2)
 * ```
 */
export const useDeliverableSetsStore = create<DeliverableSetListStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      /**
       * Set search text
       */
      setSearchText: (searchText: string) => {
        set({ searchText }, false, 'setSearchText')
      },

      /**
       * Set a filter value
       * Supports all filter fields including status (multi-select array)
       */
      setFilter: (payload: Partial<DeliverableSetFilters>) => {
        set(
          (state) => ({ filters: { ...state.filters, ...payload } }),
          false,
          'setFilter'
        )
      },

      /**
       * Set current page
       */
      setPage: (page: number) => {
        set({ page }, false, 'setPage')
      },

      /**
       * Set rows per page
       */
      setRowsPerPage: (rowsPerPage: number) => {
        set({ rowsPerPage, page: 0 }, false, 'setRowsPerPage')
      },

      /**
       * Set sort column and direction
       */
      setSort: (sort: {
        column: keyof ResponseDeliverableSet
        direction: SortOrder
      }) => {
        set({ sort }, false, 'setSort')
      },

      /**
       * Set the deliverable set being edited
       */
      setEditingDeliverableSet: (item: ResponseDeliverableSet | null) => {
        set(
          { editingDeliverableSet: item, dialogOpen: true },
          false,
          'setEditingDeliverableSet'
        )
      },

      /**
       * Clear the editing deliverable set
       */
      clearEditingDeliverableSet: () => {
        set(
          { editingDeliverableSet: null, dialogOpen: false },
          false,
          'clearEditingDeliverableSet'
        )
      },

      /**
       * Set dialog open/close state
       */
      setDialogOpen: (
        open: boolean,
        companyId?: string,
        projectId?: string
      ) => {
        set(
          {
            dialogOpen: open,
            contextCompanyId: open ? companyId || null : null,
            contextProjectId: open ? projectId || null : null,
          },
          false,
          'setDialogOpen'
        )
      },

      /**
       * Set the deliverable set being deleted
       */
      setDeletingDeliverableSet: (item: ResponseDeliverableSet | null) => {
        set(
          { deletingDeliverableSet: item, deleteDialogOpen: true },
          false,
          'setDeletingDeliverableSet'
        )
      },

      /**
       * Clear the deleting deliverable set
       */
      clearDeletingDeliverableSet: () => {
        set(
          { deletingDeliverableSet: null, deleteDialogOpen: false },
          false,
          'clearDeletingDeliverableSet'
        )
      },

      /**
       * Set delete dialog open/close state
       */
      setDeleteDialogOpen: (open: boolean) => {
        set({ deleteDialogOpen: open }, false, 'setDeleteDialogOpen')
      },

      setSendToReviewDialogOpen: (open: boolean) => {
        set(
          { showSendApprovalDialog: open },
          false,
          'setSendToReviewDialogOpen'
        )
      },

      setSendToReviewDeliverableSet: (item: ResponseDeliverableSet | null) => {
        set(
          { requestApprovalDeliverableSet: item, showSendApprovalDialog: true },
          false,
          'setSendToReviewDeliverableSet'
        )
      },

      clearSendToReviewDeliverableSet: () => {
        set(
          {
            requestApprovalDeliverableSet: null,
            showSendApprovalDialog: false,
          },
          false,
          'clearSendToReviewDeliverableSet'
        )
      },

      setCancelApprovalDialogOpen: (open: boolean) => {
        set(
          { showCancelApprovalDialog: open },
          false,
          'setCancelApprovalDialogOpen'
        )
      },

      setCancelApprovalDeliverableSet: (
        item: ResponseDeliverableSet | null
      ) => {
        set(
          {
            cancelApprovalDeliverableSet: item,
            showCancelApprovalDialog: true,
          },
          false,
          'setCancelApprovalDeliverableSet'
        )
      },

      clearCancelApprovalDeliverableSet: () => {
        set(
          {
            cancelApprovalDeliverableSet: null,
            showCancelApprovalDialog: false,
          },
          false,
          'clearCancelApprovalDeliverableSet'
        )
      },

      setDelegateDialogOpen: (open: boolean) => {
        set({ showDelegateDialog: open }, false, 'setDelegateDialogOpen')
      },

      setDelegateDeliverableSet: (item: ResponseDeliverableSet | null) => {
        set(
          { delegateDeliverableSet: item, showDelegateDialog: true },
          false,
          'setDelegateDeliverableSet'
        )
      },

      clearDelegateDeliverableSet: () => {
        set(
          { delegateDeliverableSet: null, showDelegateDialog: false },
          false,
          'clearDelegateDeliverableSet'
        )
      },

      /**
       * Reset all filters to initial state
       */
      resetFilters: () => {
        set(
          {
            filters: initialState.filters,
            page: 0,
          },
          false,
          'resetFilters'
        )
      },

      /**
       * Set invite user dialog open/close state
       */
      setInviteUserDialogOpen: (open: boolean) => {
        set(
          {
            showInviteUserDialog: open,
            inviteUserSearchText: open ? get().inviteUserSearchText : null,
          },
          false,
          'setInviteUserDialogOpen'
        )
      },

      setInviteUserSearchText: (searchText: string) => {
        set(
          { inviteUserSearchText: searchText },
          false,
          'setInviteUserSearchText'
        )
      },

      /**
       * Set invite users
       */
      setInviteUsers: (users: IInviteUserDeliverableSet[]) => {
        set({ inviteUsers: users }, false, 'setInviteUsers')
      },

      /**
       * Update invite user role
       */
      updateInviteUserRole: (payload: {
        id?: string
        uuid?: string
        roleId: string
        roleName: string
        index: number
      }) => {
        const inviteUsers = get().inviteUsers

        const user = inviteUsers.find((user) => {
          if (payload.id) return user.id === payload.id
          if (payload.uuid) return user.uuid === payload.uuid
        })

        if (!user) return

        const newInviteUsers = [...inviteUsers]
        newInviteUsers[payload.index] = {
          ...user,
          roleId: payload.roleId,
          roleName: payload.roleName,
        }

        set({ inviteUsers: newInviteUsers }, false, 'updateInviteUserRole')
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    {
      name: 'DeliverableSetListStore',
    }
  )
)
